// ── CAPR S5 — SEPARATION OF DUTIES AT APPROVAL, declared ONCE ─────────────────────────────────
//
// An organization may require that whoever SUBMITTED an assessment does not also APPROVE it
// (tenants.require_different_approver, V1089). The api decides, inside the approve transaction,
// against the submitter it recorded at submit (cts_assignments.submitted_by) — never the reassignable
// assignee, never a field of the request. These are the names it refuses with and the words every
// surface shows; a surface renders the server's refusal, it never re-derives one.
//
// ⚠ APPROVE ONLY. Returning work is never restricted by this setting: a return records no decision.
// ⚠ THE REMEDY IS A DIFFERENT APPROVER, never a different assessor. Nothing here, and nothing that
//   reads it, rewrites who submitted the work.
// ⚠ FAIL CLOSED. With the setting on, an assessment whose submitter is not recorded cannot be approved.

export const CTS_APPROVAL_SEPARATION_REFUSALS = ['approver_is_assessor', 'assessor_unrecorded'] as const;
export type CtsApprovalSeparationRefusal = typeof CTS_APPROVAL_SEPARATION_REFUSALS[number];

/** The refusal the api returns, as words — by refusal and by whether the control has a NAMED Approver. */
export function ctsApprovalSeparationMessage(refusal: CtsApprovalSeparationRefusal, namedApprover: boolean): string {
  if (refusal === 'assessor_unrecorded') {
    return 'This organization requires a different approver, and who submitted this assessment is not recorded, '
      + 'so it cannot be approved. Return it so the assessor can submit it again.';
  }
  return namedApprover
    ? 'This organization requires a different approver. You submitted this assessment, so you cannot approve it, '
      + 'and you are this control\'s named Approver: a risk manager must name a different Approver for the control '
      + 'before it can be approved. You can still return it.'
    : 'This organization requires a different approver. You submitted this assessment, so you cannot approve it: '
      + 'another eligible approver must approve it. You can still return it.';
}

/** The setting, as the settings page names it. */
export const CTS_REQUIRE_DIFFERENT_APPROVER_LABEL = 'Require a different approver';
export const CTS_REQUIRE_DIFFERENT_APPROVER_HINT =
  'When on, the person who submitted an assessment cannot approve it; another eligible approver must. '
  + 'Returning work is not affected. When off, self-approval is allowed and recorded as self-approved.';
